import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { fromEvent, map, startWith, Subscription, tap } from 'rxjs';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import { IObjectOption, SelectOption } from '../form-input-select.component';
import { ToFormControlPipe } from '../../../../pipes/to-form-control/to-form-control.pipe';
import { ToSelectOptionAttributePipe } from '../../../../pipes/to-select-option-attribute/to-select-option-attribute.pipe';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import icons from './icons.json';
import { FormInputComponent } from '../form-input.component';
import { EEntityType, ERelationType } from '@web/dtos';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';

@Component({
  selector: 'aw-icon-picker',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    ToFormControlPipe,
    ToSelectOptionAttributePipe,
    AppendTestIdPipe,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './icon-picker.component.html',
})
export class IconPickerComponent
  extends FormInputComponent
  implements OnInit, OnDestroy
{
  public readonly value: InputSignal<string | undefined> = input<string>();
  public readonly name: InputSignal<string | undefined> = input<string>();

  public readonly filteredOptions: WritableSignal<SelectOption[]> = signal([]);
  public readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly showList: WritableSignal<boolean> = signal(false);

  public readonly options: Signal<SelectOption[]> = computed(() => {
    return Object.keys(icons);
  });

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  ngOnInit(): void {
    this.ngControl.control!.addValidators(this.isValidOption());
    this.subscription.add(
      this.ngControl!.valueChanges!.pipe(
        tap(() => this.showList.set(true)),
        startWith(''),
        map(value => this.filter(value?.toString() || '')),
        tap(filteredOptions => this.filteredOptions.set(filteredOptions))
      ).subscribe()
    );

    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const filteredOptions = this.filteredOptions();
        const isCorrectTarget = event.target?.id === this.id();
        if (isCorrectTarget) {
          event.preventDefault();
          if (event.key === 'ArrowDown' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! + 1,
                    filteredOptions.length
                  )
                : 0
            );
          } else if (event.key === 'ArrowUp' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== 0 && this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! - 1,
                    filteredOptions.length
                  )
                : undefined
            );
          } else if (
            event.key === 'Enter' &&
            isCorrectTarget &&
            this.selectedItem() !== undefined &&
            this.showList()
          ) {
            this.selectOptionAndSetItem(
              filteredOptions?.[this.selectedItem()!],
              this.selectedItem()!
            );
          } else if (
            event.key === 'Backspace' &&
            isCorrectTarget &&
            '[object Object]'.includes(
              this.ngControl.control!.value.toString()
            ) &&
            !this.value()
          ) {
            this.ngControl.control!.setValue('');
          }
        } else if (!isCorrectTarget && this.showList()) {
          event.preventDefault();
          this.showList.set(false);
        }
      })
    );

    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'click'
      ).subscribe(event => {
        const isCorrectTarget =
          event?.target?.id === this.id() ||
          event?.target?.id === this.id() + '-icon-preview';
        if (!isCorrectTarget) {
          this.showList.set(false);
        } else if (isCorrectTarget && !this.showList()) {
          this.showList.set(true);
        }
      })
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl.control!.removeValidators(this.isValidOption());
  }

  public selectOption(option: SelectOption): void {
    const findValue = (option: SelectOption, value?: string) => {
      return option && value && (option as IObjectOption)[value]
        ? (option as IObjectOption)[value]
        : option;
    };
    if (this.ngControl.control) {
      this.ngControl.control!.setValue(findValue(option, this.value()));
      this.ngControl.control!.markAsTouched();
    }
  }

  public selectOptionAndSetItem(option: SelectOption, index: number): void {
    this.selectOption(option);
    this.selectedItem.set(index);
    this.showList.set(false);
  }

  private clampSelectedItem(newValue: number, maxLength: number): number {
    if (newValue >= maxLength) {
      return maxLength - 1;
    } else if (newValue < 0) {
      return 0;
    }
    return newValue;
  }

  private filter(value: string): SelectOption[] {
    const filterValue = value?.toLowerCase();
    if (this.name()) {
      return this.options().filter(option => {
        return (option as IObjectOption)[this.name()!]
          ? ((option as IObjectOption)[this.name()!] as string)
              .toString()
              .toLowerCase()
              .includes(filterValue)
          : (option as string).toString().toLowerCase().includes(filterValue);
      });
    }

    return this.options().filter(option =>
      option?.toString()?.toLowerCase().includes(filterValue)
    );
  }

  private isValidOption(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === undefined || control.value === null) {
        return null;
      }
      const isInOptions = this.options().filter(option =>
        this.value()
          ? control.value == (option as IObjectOption)[this.value()!]
          : control.value == option
      );
      return isInOptions.length === 0 ? { invalidOption: true } : null;
    };
  }
}
